import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Container, Alert, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getSubscriptionDetails,
  cancelPlan,
  getCurrentUserPlan,
  clearError,
} from "../../../redux/slices/paymentSlice";
import { plans } from "../../../utils/static";
import {
  PLAN_PRICING,
  validateApiCallsForPlan,
  calculatePlanPrice,
  getPlanPricingConfig,
  roundPlanChargeAmount,
} from "../../../utils/planPricing";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import CurrentPlanAlert from "./CurrentPlanAlert";
import PricingHeader from "./PricingHeader";
import PlanCard from "./PlanCard";
import UpgradeModal from "./UpgradeModal";
import { useLocation } from "react-router-dom";

const buildInitialApiCalls = () => {
  const initial = {};
  plans.forEach((plan) => {
    if (plan.isFree) return;
    const cfg = getPlanPricingConfig(plan.backendName);
    initial[plan.backendName] = cfg?.minCalls ?? plan.apiLimit;
  });
  return initial;
};

const buildInitialForwardRatesAddon = () => ({
  PRO: false,
  ULTIMATE: false,
});

const SubscriptionTab = () => {
  const [showModal, setShowModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [currency, setCurrency] = useState("INR");
  const [apiCallsByPlan, setApiCallsByPlan] = useState(buildInitialApiCalls);
  const [forwardRatesAddonByPlan, setForwardRatesAddonByPlan] = useState(
    buildInitialForwardRatesAddon
  );

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
  };

  const handleCurrencyToggle = (newCurrency) => {
    setCurrency(newCurrency);
  };

  const dispatch = useDispatch();
  const { subscription, currentPlan, loading, error, planLoading, planError } =
    useSelector((state) => state.payment);
  const location = useLocation();

  const planHierarchy = {
    FREE: 1,
    BASIC: 2,
    ESSENTIAL: 3,
    PRO: 4,
    ULTIMATE: 5,
  };

  const getPlanApiCalls = (plan) => apiCallsByPlan[plan.backendName];

  const updatePlanApiCalls = (backendName, value) => {
    setApiCallsByPlan((prev) => ({ ...prev, [backendName]: value }));
  };

  const updatePlanForwardRatesAddon = (backendName, enabled) => {
    setForwardRatesAddonByPlan((prev) => ({ ...prev, [backendName]: enabled }));
  };

  const buildPlanWithPricing = (plan) => {
    if (plan.isFree) {
      return {
        ...plan,
        customApiRequests: PLAN_PRICING.FREE.includedCalls,
        calculatedPrice: 0,
      };
    }
    const validation = validateApiCallsForPlan(
      plan.backendName,
      getPlanApiCalls(plan)
    );
    if (!validation.valid) {
      toast.error(validation.error);
      return null;
    }
    const price = calculatePlanPrice(plan.backendName, validation.apiCalls, {
      interval: billingCycle,
      currency,
      forwardRatesAddon: forwardRatesAddonByPlan[plan.backendName] || false,
    });
    return {
      ...plan,
      customApiRequests: validation.apiCalls,
      calculatedPrice: roundPlanChargeAmount(price.amount, currency),
      priceBreakdown: price,
      forwardRatesAddon: forwardRatesAddonByPlan[plan.backendName] || false,
    };
  };

  const handleChoosePlan = (plan) => {
    if (currentPlan && currentPlan.current_plan) {
      const currentLevel =
        planHierarchy[currentPlan.current_plan.toUpperCase()] || 0;
      const newLevel = planHierarchy[plan.backendName.toUpperCase()] || 0;

      if (newLevel < currentLevel) {
        toast.error(
          `Cannot downgrade from ${currentPlan.current_plan} to ${plan.name}. Only upgrades are allowed.`
        );
        return;
      }

      if (newLevel === currentLevel) {
        toast.info(`You are already on the ${plan.name} plan.`);
        return;
      }
    }

    const enriched = buildPlanWithPricing(plan);
    if (!enriched) return;

    setSelectedPlan(enriched);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPlan(null);
  };

  useEffect(() => {
    dispatch(getSubscriptionDetails());
    dispatch(getCurrentUserPlan());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (location.state) {
      const {
        selectedPlan: planName,
        billingCycle: cycle,
        currency: curr,
        autoSelect,
        customApiRequests,
      } = location.state;

      if (cycle) setBillingCycle(cycle);
      if (curr) setCurrency(curr);
      if (customApiRequests && planName) {
        setApiCallsByPlan((prev) => ({
          ...prev,
          [planName]: customApiRequests,
        }));
      }

      if (planName && autoSelect) {
        const plan = plans.find((p) => p.backendName === planName);
        if (plan) {
          setTimeout(() => {
            handleChoosePlan(plan);
          }, 500);
        }
      }

      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handlePayment = async (plan) => {
    try {
      console.log("Payment plan payload:", plan);
    } catch (err) {
      toast.error(err.message || "Payment failed");
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      await dispatch(cancelPlan("User requested cancellation")).unwrap();
      toast.success("Your subscription has been cancelled immediately.");
      setShowCancelModal(false);
      dispatch(getCurrentUserPlan());
    } catch {
      toast.error("Failed to cancel subscription");
    } finally {
      setCancelLoading(false);
    }
  };

  const isPlanDisabled = (plan) => {
    if (!currentPlan || !currentPlan.current_plan) return false;
    const currentLevel =
      planHierarchy[currentPlan.current_plan.toUpperCase()] || 0;
    const planLevel = planHierarchy[plan.backendName.toUpperCase()] || 0;
    return planLevel < currentLevel;
  };

  const getPlanStatus = (plan) => {
    if (!currentPlan || !currentPlan.current_plan) return "available";
    const currentLevel =
      planHierarchy[currentPlan.current_plan.toUpperCase()] || 0;
    const planLevel = planHierarchy[plan.backendName.toUpperCase()] || 0;
    if (planLevel < currentLevel) return "downgrade";
    if (planLevel === currentLevel) return "current";
    return "upgrade";
  };

  return (
    <Container fluid style={{ padding: "20px 40px" }}>
      <div className="text-center mb-4">
        <h2 className="fw-bold text-primary mb-3">Choose Your Plan</h2>
        <PricingHeader
          billingCycle={billingCycle}
          setBillingCycle={setBillingCycle}
          currency={currency}
          setCurrency={handleCurrencyToggle}
        />
      </div>

      {planLoading ? (
        <div className="text-center mb-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading plan information...
        </div>
      ) : currentPlan ? (
        <Alert variant="info" className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Current Plan:</strong> {currentPlan?.current_plan}
              {currentPlan.is_unlimited ? (
                <span className="badge bg-success ms-2">Unlimited API Calls</span>
              ) : (
                <span className="badge bg-primary ms-2">
                  {currentPlan.api_limit_remaining?.toLocaleString()} /{" "}
                  {currentPlan.monthly_api_limit?.toLocaleString()} API calls
                  remaining
                </span>
              )}
            </div>
          </div>
        </Alert>
      ) : planError ? (
        <Alert variant="warning" className="mb-4">
          Unable to load current plan information. {planError}
        </Alert>
      ) : null}

      <CurrentPlanAlert
        subscription={currentPlan}
        loading={loading}
        onCancelSubscription={() => setShowCancelModal(true)}
      />

      <CancelSubscriptionModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={handleCancelSubscription}
        loading={cancelLoading}
      />

      <div className="pricing sp pt-0">
        <Row className="g-4 mb-4">
          {plans.map((plan, index) => {
            const planStatus = getPlanStatus(plan);
            const isDisabled = isPlanDisabled(plan);
            const isWidePlan = index >= 3;

            return (
              <Col
                key={plan.name}
                lg={isWidePlan ? 6 : 4}
                md={isWidePlan ? 6 : 6}
                sm={12}
              >
                <PlanCard
                  plan={plan}
                  billingCycle={billingCycle}
                  currency={currency}
                  apiCalls={getPlanApiCalls(plan)}
                  onApiCallsChange={(value) =>
                    updatePlanApiCalls(plan.backendName, value)
                  }
                  forwardRatesAddon={
                    forwardRatesAddonByPlan[plan.backendName] || false
                  }
                  onForwardRatesAddonChange={(enabled) =>
                    updatePlanForwardRatesAddon(plan.backendName, enabled)
                  }
                  onChoosePlan={handleChoosePlan}
                  isCurrentPlan={planStatus === "current"}
                  isDisabled={isDisabled}
                  planStatus={planStatus}
                  disabledReason={isDisabled ? "Downgrades not allowed" : null}
                  priceExcludesGst
                  wide={isWidePlan}
                />
              </Col>
            );
          })}
        </Row>
      </div>

      <UpgradeModal
        show={showModal}
        onHide={handleCloseModal}
        selectedPlan={selectedPlan}
        billingCycle={billingCycle}
        currency={currency}
        subscription={subscription}
        loading={loading}
        onPayment={handlePayment}
        onCurrencyChange={handleCurrencyChange}
      />
    </Container>
  );
};

export default SubscriptionTab;
